/*
=========================================================
ASSABOOR ENTERPRISE WISHLIST V1.2
=========================================================
*/

(() => {
  'use strict';

  const STORAGE_KEY = 'assaboor-enterprise-wishlist';

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  class AEWishlist {
    constructor() {
      this.items = this.load();
      this.eventsBound = false;

      this.init();
    }

    init() {
      this.bindEvents();
      this.updateUI();

      window.addEventListener('storage', (event) => {
        if (event.key !== STORAGE_KEY) return;

        this.items = this.load();
        this.updateUI();
      });

      document.addEventListener('shopify:section:load', () => {
        this.updateUI();
      });

      document.addEventListener('shopify:section:reorder', () => {
        this.updateUI();
      });

      document.addEventListener('assaboor:products:rendered', () => {
        this.updateUI();
      });
    }

    load() {
      try {
        const stored = JSON.parse(
          localStorage.getItem(STORAGE_KEY)
        );

        return Array.isArray(stored)
          ? stored.filter((item) => item && item.id)
          : [];
      } catch (error) {
        console.warn(
          'Assaboor wishlist could not be loaded.',
          error
        );

        return [];
      }
    }

    save() {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(this.items)
        );
      } catch (error) {
        console.warn(
          'Assaboor wishlist could not be saved.',
          error
        );
      }

      this.updateUI();

      document.dispatchEvent(
        new CustomEvent('assaboor:wishlist:updated', {
          detail: {
            items: [...this.items],
            count: this.items.length
          }
        })
      );
    }

    updateUI() {
      this.updateCounters();
      this.refreshButtons();
      this.renderPage();
    }

    exists(productId) {
      if (!productId) return false;

      return this.items.some(
        (item) =>
          String(item.id) === String(productId)
      );
    }

    toggle(product) {
      if (!product || !product.id) return;

      if (this.exists(product.id)) {
        this.remove(product.id);
        return;
      }

      this.items = [
        product,
        ...this.items.filter(
          (item) =>
            String(item.id) !== String(product.id)
        )
      ];

      this.save();
    }

    remove(productId) {
      if (!productId) return;

      this.items = this.items.filter(
        (item) =>
          String(item.id) !== String(productId)
      );

      this.save();
    }

    clear() {
      this.items = [];
      this.save();
    }

    updateCounters() {
      const count = this.items.length;

      document
        .querySelectorAll('[data-ae-wishlist-count]')
        .forEach((element) => {
          element.textContent = count;
          element.hidden = count === 0;
          element.setAttribute(
            'aria-label',
            `${count} wishlist item${count === 1 ? '' : 's'}`
          );
        });
    }

    refreshButtons() {
      document
        .querySelectorAll('[data-ae-wishlist]')
        .forEach((button) => {
          const productId =
            button.dataset.productId || '';

          const active = this.exists(productId);

          button.classList.toggle('active', active);
          button.classList.toggle(
            'is-active',
            active
          );

          button.setAttribute(
            'aria-pressed',
            active ? 'true' : 'false'
          );

          const productTitle =
            button.dataset.title || 'this product';

          button.setAttribute(
            'aria-label',
            active
              ? `Remove ${productTitle} from wishlist`
              : `Add ${productTitle} to wishlist`
          );
        });
    }

    getProductFromButton(button) {
      return {
        id:
          button.dataset.productId || '',

        handle:
          button.dataset.productHandle || '',

        title:
          button.dataset.title || '',

        url:
          button.dataset.url || '',

        image:
          button.dataset.image || '',

        price:
          button.dataset.price || '',

        variant:
          button.dataset.variant || ''
      };
    }

    bindEvents() {
      if (this.eventsBound) return;

      this.eventsBound = true;

      document.addEventListener(
        'click',
        async (event) => {
          const wishlistButton =
            event.target.closest(
              '[data-ae-wishlist]'
            );

          if (wishlistButton) {
            event.preventDefault();
            event.stopPropagation();

            const product =
              this.getProductFromButton(
                wishlistButton
              );

            if (!product.id) {
              console.warn(
                'Wishlist button is missing data-product-id.'
              );

              return;
            }

            this.toggle(product);
            return;
          }

          const removeButton =
            event.target.closest(
              '[data-ae-wishlist-remove]'
            );

          if (removeButton) {
            event.preventDefault();

            this.remove(
              removeButton.dataset.productId
            );

            return;
          }

          const clearButton =
            event.target.closest(
              '[data-ae-wishlist-clear]'
            );

          if (clearButton) {
            event.preventDefault();
            this.clear();
            return;
          }

          const addButton =
            event.target.closest(
              '[data-ae-wishlist-add-cart]'
            );

          if (addButton) {
            event.preventDefault();

            await this.addToCart(
              addButton
            );
          }
        }
      );
    }

    async addToCart(button) {
      const variantId = Number(
        button.dataset.variant
      );

      if (!variantId) {
        console.warn(
          'Wishlist item is missing a valid variant ID.'
        );

        return;
      }

      const originalText =
        button.textContent.trim();

      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        const cartAddUrl =
          window.routes &&
          window.routes.cart_add_url
            ? window.routes.cart_add_url
            : '/cart/add.js';

        const response = await fetch(
          cartAddUrl,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          }
        );

        if (!response.ok) {
          const responseData =
            await response.json().catch(
              () => ({})
            );

          throw new Error(
            responseData.description ||
              responseData.message ||
              'Unable to add wishlist item to cart.'
          );
        }

        const addedItem =
          await response.json();

        button.textContent = 'Added ✓';

        document.dispatchEvent(
          new CustomEvent(
            'assaboor:cart:refresh',
            {
              detail: {
                item: addedItem
              }
            }
          )
        );

        document.dispatchEvent(
          new CustomEvent(
            'assaboor:cart:open'
          )
        );

        setTimeout(() => {
          button.disabled = false;
          button.textContent =
            originalText;
        }, 1400);
      } catch (error) {
        console.error(error);

        button.disabled = false;
        button.textContent =
          'Try again';

        setTimeout(() => {
          button.textContent =
            originalText;
        }, 1800);
      }
    }

    renderPage() {
      const wrapper =
        document.querySelector(
          '[data-ae-wishlist-page]'
        );

      if (!wrapper) return;

      if (!this.items.length) {
        wrapper.innerHTML = `
          <div class="ae-wishlist-empty">
            <div
              class="ae-wishlist-empty__icon"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                width="42"
                height="42"
              >
                <path
                  d="M20.8 4.7a5.4 5.4 0 0 0-7.6 0L12 5.9l-1.2-1.2a5.4 5.4 0 1 0-7.6 7.6L12 21l8.8-8.7a5.4 5.4 0 0 0 0-7.6z"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
            </div>

            <h2>Your Wishlist is Empty</h2>

            <p>
              Save products you love and
              they will appear here.
            </p>

            <a
              class="ae-button ae-button--primary"
              href="/collections/all"
            >
              Continue Shopping
            </a>
          </div>
        `;

        return;
      }

      wrapper.innerHTML = `
        <div class="ae-wishlist-toolbar">
          <p>
            ${this.items.length}
            item${this.items.length === 1 ? '' : 's'}
            saved
          </p>

          <button
            type="button"
            class="ae-wishlist-clear"
            data-ae-wishlist-clear
          >
            Clear wishlist
          </button>
        </div>

        <div class="ae-wishlist-grid">
          ${this.items
            .map((item) => {
              const imageMarkup = item.image
                ? `
                  <img
                    src="${escapeHtml(item.image)}"
                    alt="${escapeHtml(item.title)}"
                    loading="lazy"
                    width="600"
                    height="600"
                  >
                `
                : `
                  <div
                    class="ae-wishlist-card__placeholder"
                    aria-hidden="true"
                  ></div>
                `;

              const cartButton = item.variant
                ? `
                  <button
                    type="button"
                    class="ae-button ae-button--primary"
                    data-ae-wishlist-add-cart
                    data-variant="${escapeHtml(item.variant)}"
                  >
                    Add to Cart
                  </button>
                `
                : `
                  <a
                    class="ae-button ae-button--primary"
                    href="${escapeHtml(item.url)}"
                  >
                    View Product
                  </a>
                `;

              return `
                <article
                  class="ae-product-card ae-wishlist-card"
                  data-product-id="${escapeHtml(item.id)}"
                >
                  <a
                    href="${escapeHtml(item.url)}"
                    class="ae-product-card__image ae-wishlist-card__image"
                    aria-label="${escapeHtml(item.title)}"
                  >
                    ${imageMarkup}
                  </a>

                  <div class="ae-product-card__content">
                    <h3>
                      <a href="${escapeHtml(item.url)}">
                        ${escapeHtml(item.title)}
                      </a>
                    </h3>

                    ${
                      item.price
                        ? `
                          <p class="ae-price">
                            ${escapeHtml(item.price)}
                          </p>
                        `
                        : ''
                    }

                    <div class="ae-product-card__actions">
                      ${cartButton}

                      <button
                        type="button"
                        class="ae-button ae-button--secondary"
                        data-ae-wishlist-remove
                        data-product-id="${escapeHtml(item.id)}"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </article>
              `;
            })
            .join('')}
        </div>
      `;
    }
  }

  const initializeWishlist = () => {
    if (window.AssaboorWishlist) {
      window.AssaboorWishlist.updateUI();
      return;
    }

    window.AssaboorWishlist =
      new AEWishlist();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeWishlist,
      { once: true }
    );
  } else {
    initializeWishlist();
  }
})();